import {
  ClassGroupFilterOption,
  FilterOptions,
  SchoolFilterOption,
  SchoolGroupFilterOption,
  SubjectFilterOption,
} from "@/interfaces/filters.interface";
import { defineStore } from "pinia";
import api from "@/api";
import { useUserStore } from "./userStore";
import { useDataStore } from "./dataStore";
import { SchoolGroup } from "@/interfaces/school-group.interface";

export const useFilterStore = defineStore({
  id: "filterStore",
  state: () => ({
    loader: {
      fetchFilterOptions: false,
    },
    selectedFilters: {
      school: "",
      subject: "",
      gradeLevel: -1,
      classGroup: [] as string[],
      schoolGroup: "",
    },
    loadingMessage: "",
    filterOptions: null as FilterOptions | null,
    filtersChanged: false,
  }),
  getters: {
  
    selectedSchoolGroup(): SchoolGroupFilterOption {
      return this.filterOptions?.schoolGroups.find(
        (sg) => sg._id === this.selectedFilters.schoolGroup
      ) as SchoolGroupFilterOption;
    },
    selectedSchool(): SchoolFilterOption {
      return this.selectedSchoolGroup?.schools.find(
        (school) => school._id == this.selectedFilters.school
      ) as SchoolFilterOption;
    },
    selectedGrade(): number {
      return this.selectedFilters.gradeLevel;
    },
    preselectedGrade(): number {
      return this.selectedFilters.gradeLevel;
    },
    selectedClassGroups(): string[] {
      return this.selectedFilters.classGroup;
    },
    preselectedClassGroups(): string[] {
      return this.selectedFilters.classGroup;
    },
    selectedSubject(): SubjectFilterOption {
      return this.filterOptions?.subjects.find(
        (subject) => subject._id === this.selectedFilters.subject
      ) as SubjectFilterOption;
    },
    /**
     * Getter to flatten and return all class groups (classes) across all schools and subjects.
     * Each class group is returned as-is without modifying its properties.
     */
    allClassGroups(): ClassGroupFilterOption[] {
      if (!this.filterOptions) return [];

      return this.filterOptions.schoolGroups.flatMap((schoolGroup) =>
        schoolGroup.schools.flatMap((school) => school.classes)
      );
    },
    filtersAreDifferent(): boolean {
      return (
        JSON.stringify(this.selectedFilters) !==
        JSON.stringify(this.selectedFilters)
      );
    },
    schoolGroupOptions(): { id: string; name: string }[] {
      return (
        this.filterOptions?.schoolGroups.map((schoolGroup) => ({
          id: schoolGroup._id,
          name: schoolGroup.name,
        })) || []
      );
    },
    schoolOptions(): { id: string; name: string }[] {
      return (
        this.filterOptions?.schoolGroups
          .find((sg) => sg._id === this.selectedFilters.schoolGroup)
          ?.schools.map((school) => ({
            id: school._id,
            name: school.name,
          })) || []
      );
    },
    subjectOptions(): { id: string; name: string }[] {
      return (
        this.filterOptions?.subjects.map((subject) => ({
          id: subject._id,
          name: subject.name,
          icon: subject.name == "Maths" ? "rl-math-icon" : "rl-english-icon",
        })) || []
      );
    },
    selectedSchoolGrades(): number[] {
      return this.selectedSchool?.grades || [];
    },
    selectedSchoolClasses(): ClassGroupFilterOption[] {
      return this.selectedSchool?.classes || [];
    },
    classesBySubjectAndGrade(): ClassGroupFilterOption[] {
      const classes = this.selectedSchoolClasses.filter(
        (cls) =>
          cls.subject === this.selectedFilters.subject &&
          (cls.gradeLevel === this.selectedFilters.gradeLevel ||
            cls.gradeLevel === this.selectedFilters.gradeLevel)
      );

      return classes;
    },
  },
  actions: {
    async fetchUpdatedFilters() {
      try {
        const userStore = useUserStore();
        const response = await api.get(
          `/v2/access/filters/options/${userStore.user?._id}`
        );
        if (response.data.success) {
          const currentFilters = {...this.selectedFilters};
          this.filterOptions = response.data.data;
          this.selectedFilters.subject = currentFilters.subject;
          this.selectedFilters.schoolGroup = currentFilters.schoolGroup;
          const selectedSchoolGroup = currentFilters.schoolGroup
          if (selectedSchoolGroup) {
            this.selectedFilters.school = currentFilters.school;
            this.selectedFilters.gradeLevel = currentFilters.gradeLevel
          }
        }
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    },
    async fetchFilterOptions(initialiseFilters = false) {
      try {
        const userStore = useUserStore();

        const response = await api.get(
          `/v2/access/filters/options/${userStore.user?._id}`
        );

        if (response.data.success) {
          this.filterOptions = response.data.data;

          // if class group admin select appropriate filters
          if (userStore.isClassGroupAdmin && initialiseFilters) {
            const adminClassGroupIds = userStore.getAdminClassGroupIds;

            if (adminClassGroupIds.length > 0) {
              const selectedClassGroupId = adminClassGroupIds[0];
              const selectedClassGroup = this.allClassGroups.find(
                (cls) => cls._id == selectedClassGroupId
              );

              this.selectedFilters.classGroup = [selectedClassGroupId];
              this.selectedFilters.subject =
                selectedClassGroup?.subject as string;
              this.selectedFilters.gradeLevel =
                selectedClassGroup?.gradeLevel as number;

              // Find the School that contains the selected Class Group
              const selectedSchool = this.filterOptions?.schoolGroups
                .flatMap((sg) => sg.schools)
                .find((school) =>
                  school.classes.some((cls) => cls._id === selectedClassGroupId)
                );

              if (selectedSchool) {
                // Set School
                this.selectedFilters.school = selectedSchool._id;

                // Find the School Group that contains the selected School
                const selectedSchoolGroup =
                  this.filterOptions?.schoolGroups.find((sg) =>
                    sg.schools.some(
                      (school) => school._id === selectedSchool._id
                    )
                  );

                if (selectedSchoolGroup) {
                  // Set School Group
                  this.selectedFilters.schoolGroup = selectedSchoolGroup._id;
                }
              }
            } else {
              this.selectedFilters.subject = this.filterOptions?.subjects[0]
                ._id as string;

              // Find the first School Group that has schools
              this.selectedFilters.schoolGroup = this.filterOptions?.schoolGroups
                .find((sg) => sg.schools.length > 0)
                ?._id as string;

              // Set School
              const selectedSchoolGroup = this.filterOptions?.schoolGroups.find(
                (sg) => sg._id === this.selectedFilters.schoolGroup
              );

              if (selectedSchoolGroup) {
                this.selectedFilters.school = selectedSchoolGroup.schools[0]
                  ._id as string;

                this.selectedFilters.gradeLevel =
                  selectedSchoolGroup.schools[0].grades[0];
              }
            }
          } else {
            this.selectedFilters.subject = this.filterOptions?.subjects[0]
              ._id as string;

            // Find the first School Group that has schools
            this.selectedFilters.schoolGroup = this.filterOptions?.schoolGroups
              .find((sg) => sg.schools.length > 0)
              ?._id as string;

            // Set School
            const selectedSchoolGroup = this.filterOptions?.schoolGroups.find(
              (sg) => sg._id === this.selectedFilters.schoolGroup
            );

            if (selectedSchoolGroup) {
              this.selectedFilters.school = selectedSchoolGroup.schools[0]
                ._id as string;

              this.selectedFilters.gradeLevel =
                selectedSchoolGroup.schools[0].grades[0];
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    },
    updateSchoolGroup(updatedSchoolGroup: any) {
      this.filterOptions?.schoolGroups.forEach((group: any) => {
        // Find the index of the updated school group in the array
        const index = this.filterOptions?.schoolGroups.findIndex(
          (group: any) => group._id === updatedSchoolGroup._id
        );
  
        if (index !== undefined && index !== -1) {
          // Replace the old object with the new updated object
          if (this.filterOptions) {
            this.filterOptions.schoolGroups[index] = updatedSchoolGroup;
          }
        } else {
          // If it doesn't exist, add it (fallback case)
          this.filterOptions?.schoolGroups?.push(updatedSchoolGroup);
        }
      });
    },
    addSchoolToSchoolGroup(school: any, schoolGroupId: string, loadSchool: boolean): void {
      const targetGroup = this.filterOptions?.schoolGroups.find((sg) => sg._id === schoolGroupId);
    
      if (targetGroup) {
        // Ensuring the school doesn't already exist in the group before pushing
        if (!targetGroup.schools.some((existingSchool) => existingSchool._id === school._id)) {
          targetGroup.schools.push(school);
          if (loadSchool === true) {
            this.selectedFilters.school = school._id;
            this.applyFilters();
          }
        } else {
          console.error(`School ${school.name} already exists in the group.`);
        }
      } else {
        console.error(`School group with ID ${schoolGroupId} not found.`);
      }
    },
    addSchoolGroup(schoolGroup: any): void {
      if (!this.filterOptions?.schoolGroups.some((group) => group._id === schoolGroup._id)) {
        this.filterOptions?.schoolGroups.push(schoolGroup);
      } else {
        console.error(`School group with ID ${schoolGroup._id} already exists.`);
      }
    },
    applyFilters() {
      const dataStore = useDataStore();
      dataStore.fetchInitialData();

      this.filtersChanged = false;
    },
    selectSchoolGroup(schoolGroup: string) {
      if (schoolGroup) {
        this.selectedFilters.schoolGroup = schoolGroup;
      }

      // Select the first school in the school group
      const selectedSchoolGroup = this.filterOptions?.schoolGroups.find(
        (sg) => sg._id === schoolGroup
      );

      if (selectedSchoolGroup) {
        if (selectedSchoolGroup.schools.length > 0) {
          this.selectedFilters.school = selectedSchoolGroup.schools[0]._id;
          this.selectedFilters.gradeLevel = selectedSchoolGroup.schools[0].grades[0];
        }
      }

      this.filtersChanged = true;
    },
    selectSchool(school: string) {
      if (school) {
        this.selectedFilters.school = school;
      }

      // Select the first grade in the school
      const selectedSchool = this.selectedSchool;

      if (selectedSchool && selectedSchool.grades && selectedSchool.grades.length > 0) {
        this.selectedFilters.gradeLevel = selectedSchool.grades[0];
      } else {
        this.selectedFilters.gradeLevel = -1; // or any default value you prefer
      }

      this.filtersChanged = true;
    },
    selectClassGroups(classGroups: string[]) {
      this.selectedFilters.classGroup = classGroups;
    },
    toggleClassGroup(classGroupId: string) {
      const selectedIndex = this.selectedFilters.classGroup.indexOf(classGroupId);

      if (selectedIndex > -1) {
        // Remove from selected if already selected
        this.selectedFilters.classGroup.splice(selectedIndex, 1);
      } else {
        // Add to selected if not found
        this.selectedFilters.classGroup.push(classGroupId);
      }

      this.filtersChanged = true;
    },
    selectGradeLevel(gradeLevel: number) {
      this.selectedFilters.gradeLevel = gradeLevel;
      this.filtersChanged = true;
    },
    selectSubject(subject: string) {
      this.selectedFilters.subject = subject;
    },
    resetFilters() {
      this.selectedFilters.school = "";
      this.selectedFilters.gradeLevel = -1;
      this.selectedFilters.classGroup = [];
      this.selectedFilters.subject = "";
      this.selectedFilters.schoolGroup = "";
    },
    clearGradeLevelFilter() {
      this.selectedFilters.gradeLevel = -1;
      this.selectedFilters.classGroup = [];
    },
    clearClassGroupFilter() {
      this.selectedFilters.classGroup = [];
    },
  },
  persist: true,
});
